import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Slider from 'react-slick';
import {
    useGetMyWorksitesMutation,
    useGetWorksiteProgressMutation,
    useGetWorksiteInfoMutation,
    GetWorksiteProgressResponse,
    GetWorksiteInfoResponse
} from '@services';

export type WorksiteWithData = {
    id: number;
    progress: GetWorksiteProgressResponse;
    info: GetWorksiteInfoResponse;
};

type TaskData = {
    name: string;
    total: number;
    consummed: number;
    percent_consumed: number;
    status: string;
};

export const useWorksiteResultsPageVM = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const worksiteId = searchParams.get('worksiteId');

    const [getMyWorksites, { data: worksitesData, isLoading: isLoadingWorksites }] = useGetMyWorksitesMutation();
    const [getWorksiteProgress] = useGetWorksiteProgressMutation();
    const [getWorksiteInfo] = useGetWorksiteInfoMutation();

    const [worksitesWithData, setWorksitesWithData] = useState<WorksiteWithData[]>([]);
    const [isLoadingProgress, setIsLoadingProgress] = useState(false);

    const [, setNav1] = useState<Slider | null>(null);
    const [currentIndex, setCurrentIndex] = useState<number>();

    // Récupérer la liste des chantiers au chargement
    useEffect(() => {
        getMyWorksites();
    }, [getMyWorksites]);

    // Pour chaque chantier, récupérer les données de progression et les infos
    const fetchWorksitesData = useCallback(async () => {
        if (worksitesData && worksitesData.length > 0) {
            setIsLoadingProgress(true);
            const worksitePromises = worksitesData.map(async (worksite) => {
                try {
                    const [progressResponse, infoResponse] = await Promise.all([
                        getWorksiteProgress({ worksiteId: worksite.id }).unwrap(),
                        getWorksiteInfo(worksite.id.toString()).unwrap()
                    ]);

                    return {
                        id: worksite.id,
                        progress: progressResponse,
                        info: infoResponse
                    };
                } catch (error) {
                    console.error(`Erreur lors de la récupération des données du chantier ${worksite.id}:`, error);
                    return null;
                }
            });

            const results = await Promise.all(worksitePromises);
            const validResults = results.filter((result): result is WorksiteWithData => result !== null);
            setWorksitesWithData(validResults);
            setIsLoadingProgress(false);
        }
    }, [worksitesData, getWorksiteProgress, getWorksiteInfo]);

    useEffect(() => {
        fetchWorksitesData();
    }, [fetchWorksitesData]);

    // Déterminer l'index du chantier sélectionné
    const worksiteIndex = useMemo(
        () => worksitesWithData?.findIndex((worksite) => worksite.progress.worksite.id_ext === worksiteId),
        [worksitesWithData, worksiteId]
    );

    // Mettre à jour l'index courant quand le chantier est trouvé
    useEffect(() => {
        if (worksiteIndex !== -1 && worksiteIndex !== currentIndex) {
            setCurrentIndex(worksiteIndex);
        }
    }, [worksiteIndex, currentIndex]);

    // Chantier courant
    const currentWorksite = useMemo(
        () => (currentIndex !== undefined ? worksitesWithData?.[currentIndex] : undefined),
        [worksitesWithData, currentIndex]
    );

    // Extraire les tâches du chantier courant
    const tasks = useMemo(() => {
        if (!currentWorksite?.progress?.detail) return [];

        const tasksList: TaskData[] = [];
        const detail = currentWorksite.progress.detail;

        // Parcourir toutes les tâches dans l'objet detail
        Object.keys(detail).forEach((key) => {
            const task = detail[key];
            // Ne garder que les tâches qui sont des objets avec des données complètes
            if (typeof task === 'object' && 'total' in task && 'consummed' in task) {
                tasksList.push({
                    name: key,
                    total: task.total,
                    consummed: task.consummed,
                    percent_consumed: task.percent_consumed,
                    status: task.status
                });
            }
        });

        return tasksList;
    }, [currentWorksite]);

    // Mettre à jour l'URL quand on change de chantier
    useEffect(() => {
        if ((currentIndex || currentIndex === 0) && worksitesWithData.length > 0 && worksitesWithData[currentIndex]) {
            setSearchParams({ worksiteId: worksitesWithData[currentIndex].progress.worksite.id_ext }, { replace: true });
        }
    }, [currentIndex, worksitesWithData, setSearchParams]);

    const isLoading = isLoadingWorksites || isLoadingProgress;

    return {
        worksitesWithData,
        currentWorksite,
        tasks,
        setNav1,
        currentIndex,
        setCurrentIndex,
        isLoading
    };
};
